import React, { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useSchoolData } from './SchoolDataProvider';
import { groupUnplaced, sessionCount, DAY_LABELS } from './placement';
import { BLOCK_COLOR } from './blockTheme';

// Droppable id for the pool. A string that can't collide with the day columns,
// whose ids are stringified 1..5 (see WeekBoard's Number() coercion).
export const POOL_ID = 'pool';

// Work with no day yet. This is the Monday ritual — the seed drops 17 sessions
// into the pool every week — so it is deliberately the first thing on the week
// screen rather than a separate tab the student has to remember to visit.

// ─── WEEK VARIANT ─────────────────────────────────────────────────────────────

export default function NeedsADayStrip() {
  const { unplacedThisWeek, instructionalDows, windowsDoc, today } = useSchoolData();
  const { setNodeRef, isOver } = useDroppable({ id: POOL_ID });
  const [openKey, setOpenKey] = useState(null);
  const [hidden, setHidden] = useState(false);

  const { groups, singles } = groupUnplaced(unplacedThisWeek);
  const total = sessionCount(groups) + singles.length;
  const rows = [...groups, ...singles.map(a => ({ key: a.id, sample: a, members: [a], count: 1, single: true }))];

  // Weeks 11 and 15 are two-day weeks (Thanksgiving, winter break) but the seed
  // still emits a full 17 tiles for them. Say so rather than letting the
  // student discover it by running out of days.
  const shortWeek = instructionalDows.length > 0 && instructionalDows.length < 5 && total > 0;

  return (
    <>
      <div className="db-eyebrow" style={{ marginTop: 8, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Needs a day</span>
        {total > 0 && (
          <button type="button" className="db-detail-toggle" style={{ padding: 0, marginLeft: 'auto' }}
            onClick={() => setHidden(h => !h)}>
            {hidden ? 'Show' : 'Hide'}
          </button>
        )}
      </div>

      {total > 0 && !hidden && (
        <p className="db-item-sub" style={{ margin: '0 0 8px' }}>
          {rows.length} kind{rows.length === 1 ? '' : 's'} of work · {total} session{total === 1 ? '' : 's'} still need a day.
          Drag one onto a day, or tap it to pick.
        </p>
      )}

      {shortWeek && !hidden && (
        <p style={{ color: 'var(--bad)', fontSize: 13, margin: '0 0 8px' }}>
          ⚠️ This is a {instructionalDows.length}-day week — {total} sessions won't all fit. Ask a parent what to skip.
        </p>
      )}

      <div
        ref={setNodeRef}
        className={`db-drop needs-day-strip ${isOver ? 'db-drop--over' : ''}`}
        style={{ marginBottom: 20 }}
      >
        {total === 0 ? (
          <p className="db-item-sub" style={{ margin: 0 }}>
            Everything's on a day ✓ — drop something here to take it off.
          </p>
        ) : hidden ? (
          <p className="db-item-sub" style={{ margin: 0 }}>{total} session{total === 1 ? '' : 's'} hidden.</p>
        ) : (
          rows.map(g => (
            <DraggableGroup
              key={g.key}
              group={g}
              open={openKey === g.key}
              onToggle={() => setOpenKey(k => (k === g.key ? null : g.key))}
              windows={g.sample.needsParent && windowsDoc?.date === today ? windowsDoc.windows : null}
            />
          ))
        )}
      </div>
    </>
  );
}

function DraggableGroup({ group, open, onToggle, windows }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: group.sample.id, data: { item: group.sample } });

  return (
    <div
      ref={setNodeRef}
      className="db-card needs-day-tile"
      style={{ borderLeft: `3px solid ${BLOCK_COLOR[group.sample.block] || 'var(--ink-3)'}`, opacity: isDragging ? 0.35 : 1 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="db-grip"
          aria-label={`Move ${group.sample.title} onto a day`}
          {...listeners}
          {...attributes}
        >
          ⠿
        </button>
        <button type="button" className="needs-day-tile__name" onClick={onToggle}>
          {group.sample.title}{group.count > 1 && <strong> ×{group.count}</strong>}
        </button>
        <span className="db-mins">{group.sample.estMinutes}m</span>
        <button type="button" className="db-detail-toggle" onClick={onToggle} aria-expanded={open}>
          {open ? '▲' : '▼'}
        </button>
      </div>
      {group.single && group.sample.studentQuestion && (
        <div className="db-item-sub" style={{ color: 'var(--bad)' }}>🚩 {group.sample.studentQuestion}</div>
      )}
      {open && <DayPicker item={group.sample} windows={windows} subLabel={group.sample.subLabel} />}
    </div>
  );
}

// ─── TODAY VARIANT ────────────────────────────────────────────────────────────

const TODAY_LIMIT = 4;

// On Today the question isn't "how do I lay out my week", it's "what can I do
// right now" — so this shows only a handful, ranked by what actually fits
// today, with one tap to claim it. The full list lives on the week view.
export function NeedsADayCallout({ date, onSeeWeek }) {
  const { unplacedThisWeek, place, today, warningsFor, dateForDay, canPlaceOn } = useSchoolData();
  const [openKey, setOpenKey] = useState(null);

  // Only offer this while looking at today. A tile can never move between
  // weeks — firestore.rules' placement path can't write weekNumber — and
  // "Put on today" while browsing last Thursday is a bug factory.
  if (date !== today) return null;

  const { groups, singles } = groupUnplaced(unplacedThisWeek);
  const rows = [...groups, ...singles.map(a => ({ key: a.id, sample: a, members: [a], count: 1 }))];
  if (rows.length === 0) return null;

  const todayDow = [1, 2, 3, 4, 5].find(d => dateForDay(d) === today);
  if (!todayDow || !canPlaceOn(todayDow)) return null;

  // Easiest-to-say-yes-to first: nothing that avoids today, no sequencing
  // nudges, then shortest.
  const ranked = rows
    .map(g => ({ ...g, warnings: warningsFor(g.sample, todayDow) }))
    .sort((a, b) =>
      (a.sample.avoidDays?.includes(todayDow) ? 1 : 0) - (b.sample.avoidDays?.includes(todayDow) ? 1 : 0) ||
      a.warnings.length - b.warnings.length ||
      (a.sample.estMinutes || 0) - (b.sample.estMinutes || 0)
    );
  const shown = ranked.slice(0, TODAY_LIMIT);
  const rest = ranked.length - shown.length;

  return (
    <section className="db-card needs-day-callout">
      <div className="db-eyebrow" style={{ marginBottom: 6 }}>
        Needs a day · {rows.reduce((s, g) => s + g.count, 0)} session
        {rows.reduce((s, g) => s + g.count, 0) === 1 ? '' : 's'}
      </div>
      {shown.map(g => (
        <div key={g.key} className="needs-day-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="db-item-title">
              {g.sample.title}{g.count > 1 && <strong> ×{g.count}</strong>}
            </div>
            <div className="db-item-sub">
              {[`${g.sample.estMinutes}m`, g.sample.needsParent && '🧑 needs a parent'].filter(Boolean).join(' · ')}
            </div>
            {g.warnings.length > 0 && (
              <div className="db-item-sub" style={{ color: 'var(--bad)' }}>⚠️ {g.warnings.join(' ')}</div>
            )}
          </div>
          <button type="button" className="btn btn--sm" onClick={() => place(g.sample, todayDow)}>
            Put on today
          </button>
          <button type="button" className="db-detail-toggle" aria-expanded={openKey === g.key}
            onClick={() => setOpenKey(k => (k === g.key ? null : g.key))}>
            {openKey === g.key ? '▲' : '▼'}
          </button>
          {openKey === g.key && <DayPicker item={g.sample} />}
        </div>
      ))}
      {rest > 0 && (
        <button type="button" className="db-detail-toggle" style={{ padding: '6px 0 0' }} onClick={onSeeWeek}>
          +{rest} more — plan the week ›
        </button>
      )}
    </section>
  );
}

// ─── SHARED ───────────────────────────────────────────────────────────────────

// Mon-Fri buttons — the tap path that works everywhere drag doesn't (a phone,
// a trackpad someone finds fiddly). Holidays are disabled rather than hidden,
// so the week still reads as five days.
function DayPicker({ item, windows, subLabel }) {
  const { place, warningsFor, holidayFor, dateForDay, pendingIds } = useSchoolData();
  const busy = pendingIds.has(item.id);

  return (
    <div className="needs-day-picker">
      {subLabel && <div className="db-item-sub">{subLabel}</div>}
      {windows?.length > 0 && (
        <div className="db-item-sub" style={{ color: 'var(--good)' }}>Today's windows: {windows.join(', ')}</div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5].map(dow => {
          const holiday = holidayFor(dateForDay(dow));
          const warnings = holiday ? [`${holiday} — no school`] : warningsFor(item, dow);
          return (
            <button
              key={dow}
              type="button"
              className="db-chip db-chip--button"
              disabled={busy || Boolean(holiday)}
              title={warnings.join(' ')}
              onClick={() => place(item, dow)}
            >
              {DAY_LABELS[dow]}{!holiday && warnings.length > 0 ? ' ⚠️' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
